<template>
  <ProSpaceGeoLayout v-if="model" class="adjustment-item">
    <template #top>
      <ProSpaceTopPanel :header="layoutName" back @back="backHandler">
        <template #right>
          <ProSpaceAction
            v-if="
              !isProceeded &&
              checkAccess('adjustment-proceed') &&
              (model.status === 'onacceptance' || model.status === 'onapproval')
            "
            icon="double-check"
            :text="$t('saveAndClose')"
            :disabled="loading || mixinLoadingAccess || isSaveDisabled"
            @click="saveAndClose"
          />
        </template>
      </ProSpaceTopPanel>
    </template>
    <template #center>
      <ProSpaceGeoLayout
        :is-templated="true"
        :template-center="true"
        :template-right="true"
      >
        <template #center>
          <ProSpaceTabs
            v-model="modelTab"
            :tabs="tabs"
            :loading="loading"
            style="row-gap: 15px"
          >
            <ProSpaceHLayout v-if="checkAccess('adjustment-proceed')" :gap="5">
              <ProSpaceAction
                v-if="
                  !isProceeded && modelTab.systemName == 'adjustmentRequest'
                "
                :disabled="isCancelDisabled"
                :text="$t('cancelTxt')"
                @click="backHandler"
              />
              <ProSpaceAction
                v-if="
                  !isProceeded && modelTab.systemName == 'adjustmentRequest'
                "
                icon="check"
                :disabled="isSaveDisabled"
                :text="$t('save')"
                @click="save()"
              />
              <ProSpaceDots
                v-if="
                  showDotsInPanel && modelTab.systemName == 'adjustmentRequest'
                "
                class="adjustment-item__dots"
                :options="dotsOptions"
                @action="(action) => handleDots(action)"
              />
            </ProSpaceHLayout>
            <template v-slot:tab-1>
              <ProSpaceMainGrid
                ref="grid"
                class="adjustment-item__view-adjustment"
                :cleanRecords="getAdjustmentEditedFields"
                localSort
                isInForm
                selectionMode="none"
                defaultSortField="name"
                dataKey="key"
              >
                <template #columns>
                  <Column
                    class="adjustment-item__status-cell"
                    field="name"
                    :header="$t('adjustmentEditedFields')"
                    sortable
                    :style="checkAccessAcceptance || checkAccessApproval ? 'max-width: 200px; min-width: 200px;': ''"
                  >
                    <template #body="field">
                      <ProSpaceTableHeaderCell
                        v-if="field.data"
                        noHover
                        :title="field.data.name"
                        align-items="center"
                        :tagBigText="false"
                      />
                    </template>
                  </Column>

                  <Column :header="$t('adjustmentsCurrentValue')">
                    <template #body="{ data }">
                      <ProSpaceTextBlock
                        class="adjustment-item__values"
                        type="five"
                        :gap="2"
                        :top="$t('adjustmentOldValue')"
                        :title="getFieldOldValue(data)"
                        :bottom="getFieldOldValue(data)"
                      />
                    </template>
                  </Column>

                  <Column :header="$t('adjustmentsNewValue')">
                    <template #body="{ data }">
                      <ProSpaceTextBlock
                        class="adjustment-item__values"
                        type="five"
                        :gap="2"
                        :top="$t('adjustmentNewValue')"
                        :title="getFieldNewValue(data)"
                        :bottom="getFieldNewValue(data)"
                      />
                    </template>
                  </Column>

                  <Column
                    :header="$t('acceptance')"
                    class="adjustment-item__acceptance-cell"
                    :style="checkAccessAcceptance ? 'min-width: 240px' : ''"
                  >
                    <template #body="field">
                      <ProSpaceHLayout :gap="10">
                        <ProSpaceSelectButton
                          v-if="checkAccessAcceptance"
                          class="adjustments-grid__select-btn"
                          v-model="
                            editedFields.find((i) => i.key === field.data.key)[
                              'acceptanceStatus'
                            ]
                          "
                          :items="[
                            {
                              value: 'false',
                              label: this.$t('adjustmentBlockDecline'),
                              icon: 'close',
                            },
                            {
                              value: 'true',
                              label: this.$t('adjustmentBlockAccept'),
                              icon: 'check',
                            },
                          ]"
                        />
                        <ProSpaceTag
                          :type="
                            field.data?.acceptanceStatus ? 'success' : 'error'
                          "
                          :text="
                            field.data?.acceptanceStatus
                              ? $t('adjustmentFieldAccepted')
                              : $t('adjustmentFieldDeclined')
                          "
                          v-else-if="
                            model.status !== 'onacceptance' &&
                            field.data?.acceptanceStatus !== null
                          "
                        />
                      </ProSpaceHLayout>
                    </template>
                  </Column>

                  <Column
                    :header="$t('approval')"
                    class="adjustment-item__acceptance-cell"
                    :style="checkAccessApproval ? 'min-width: 240px' : ''"
                  >
                    <template #body="field">
                      <ProSpaceHLayout :gap="10">
                        <ProSpaceSelectButton
                          v-if="checkAccessApproval"
                          class="adjustments-grid__select-btn"
                          v-model="
                            editedFields.find((i) => i.key === field.data.key)[
                              'approvalStatus'
                            ]
                          "
                          :items="[
                            {
                              value: 'false',
                              label: this.$t('adjustmentBlockDecline'),
                              icon: 'close',
                            },
                            {
                              value: 'true',
                              label: this.$t('adjustmentBlockAccept'),
                              icon: 'check',
                            },
                          ]"
                        />
                        <ProSpaceTag
                          v-else-if="
                            model.status !== 'onapproval' &&
                            field.data?.approvalStatus !== null
                          "
                          :type="
                            field.data?.approvalStatus ? 'success' : 'error'
                          "
                          :text="
                            field.data?.approvalStatus
                              ? $t('adjustmentFieldAccepted')
                              : $t('adjustmentFieldDeclined')
                          "
                        />
                      </ProSpaceHLayout>
                    </template>
                  </Column>
                </template>
                <template #empty>
                  <ProSpaceEmptyGrid
                    icon="error-message"
                    :title="$t('noDataTitle')"
                    :text="$t('noDataTxt')"
                  />
                </template>
              </ProSpaceMainGrid>
              <ProSpaceModal
                v-model="adjustmentCommentModalShow"
                icon="plus"
                :key="`adjustment-сomment-${adjustmentCommentModalShow}`"
                :title="$t('adjustmentComment')"
                :width="405"
                :cancelTxt="$t('cancelTxt')"
                :applyTxt="$t('adjustmentCommentSend')"
                :validate-local="$t"
                :validate-model="adjustmentCommentModel"
                :validate-config="{
                  comment: {
                    required,
                  },
                }"
                @apply="addComment"
              >
                <template #default="{ v }">
                  <ProSpaceVLayout :gap="0">
                    <ProSpaceInputText
                      :label="$t('adjustmentComment')"
                      v-model="adjustmentCommentModel.comment"
                      :placeholder="$t('adjustmentComment')"
                      :invalid="v.comment.$error"
                      :invalid-messages="v.comment.$errors"
                      isTextarea
                      :rows="3"
                      @blur="v.comment.$touch"
                    />
                  </ProSpaceVLayout>
                </template>
              </ProSpaceModal>
              <ProSpaceTabs
                v-model="modelTab1"
                :tabs="tabs1"
                :loading="loading"
                class="adjustment-item__inner-tabs"
                style="row-gap: 15px; margin-top: 20px"
              >
                <template v-slot:tab-1>
                  <ProSpaceVLayout
                    class="adjustments-params__rows adjustments-params__rows--details"
                  >
                    <ProSpaceGeoLayout
                      class="geo-row"
                      isHideBottom
                      isHideTop
                      isHideRight
                      :gap="60"
                    >
                      <template v-slot:left>
                        <ProSpaceTextBlock
                          class="adjustments-params__left-block"
                          :top="$t('addressProgramAgencyParamsInfo')"
                          :bottom="$t('addressProgramAgencyParamsDescription')"
                          :gap="10"
                          break-word
                        />
                      </template>
                      <template v-slot:center>
                        <ProSpaceVLayout :gap="20">
                          <ProSpaceHLayout
                            justify-content="flex-start"
                            align-items="flex-start"
                            :gap="20"
                          >
                            <ProSpaceVLayout :gap="10">
                              <ProSpaceTextBlock
                                :top="$t('agency')"
                                :bottom="model.agencyValue"
                                :gap="gapTextBlockCenter"
                                type="six"
                                break-word
                              />
                            </ProSpaceVLayout>
                            <ProSpaceVLayout :gap="10">
                              <ProSpaceTextBlock
                                :top="$t('network')"
                                :bottom="model.networkValue"
                                :gap="gapTextBlockCenter"
                                type="six"
                                break-word
                              />
                            </ProSpaceVLayout>
                            <ProSpaceVLayout :gap="10">
                              <ProSpaceTextBlock
                                :top="$t('address')"
                                :bottom="model.address"
                                :gap="gapTextBlockCenter"
                                type="six"
                                break-word
                              />
                            </ProSpaceVLayout>
                          </ProSpaceHLayout>
                          <ProSpaceHLayout
                            justify-content="flex-start"
                            align-items="flex-start"
                            :gap="20"
                          >
                            <ProSpaceVLayout :gap="10">
                              <ProSpaceTextBlock
                                :top="$t('externalCode')"
                                :bottom="model.externalCode"
                                :gap="gapTextBlockCenter"
                                type="six"
                                break-word
                              />
                            </ProSpaceVLayout>
                            <ProSpaceVLayout :gap="10">
                              <ProSpaceTextBlock
                                :top="$t('kadl')"
                                :bottom="model.kadlValue"
                                :gap="gapTextBlockCenter"
                                type="six"
                                break-word
                              />
                            </ProSpaceVLayout>
                            <ProSpaceVLayout :gap="10">
                              <ProSpaceTextBlock
                                :top="$t('kAS')"
                                :bottom="model.kasValue"
                                :gap="gapTextBlockCenter"
                                type="six"
                                break-word
                              />
                            </ProSpaceVLayout>
                          </ProSpaceHLayout>
                        </ProSpaceVLayout>
                      </template>
                    </ProSpaceGeoLayout>
                  </ProSpaceVLayout>
                </template>
              </ProSpaceTabs>
            </template>
            <template v-slot:tab-2>
              <ProSpaceVLayout class="adjustments-params__rows">
<!--                Параметры ТТ-->
                <ProSpaceGeoLayout
                  class="geo-row"
                  style="padding-top: 10px !important;"
                  isHideBottom
                  isHideTop
                  isHideRight
                  :gap="60"
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      class="adjustments-params__left-block"
                      :top="$t('addressProgramOutletInfo')"
                      :bottom="$t('addressProgramOutletDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceHLayout
                      justify-content="flex-start"
                      align-items="flex-start"
                      :gap="20"
                    >
                      <ProSpaceVLayout :gap="10">
                        <ProSpaceTextBlock
                          :top="$t('network')"
                          :bottom="outletData.networkValue"
                          :gap="gapTextBlockCenter"
                          break-word
                          type="six"
                        />
                      </ProSpaceVLayout>
                      <ProSpaceVLayout :gap="10">
                        <ProSpaceTextBlock
                          :top="$t('address')"
                          :bottom="outletData.address"
                          :gap="gapTextBlockCenter"
                          break-word
                          type="six"
                        />
                      </ProSpaceVLayout>
                      <ProSpaceVLayout :gap="10">
                        <ProSpaceTextBlock
                          :top="$t('coverage')"
                          :bottom="outletData.coverage ? $t('yes') : $t('no')"
                          :gap="gapTextBlockCenter"
                          break-word
                          type="six"
                        />
                      </ProSpaceVLayout>
                    </ProSpaceHLayout>
                  </template>
                </ProSpaceGeoLayout>
<!--                Географические параметры -->
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  isHideRight
                  :gap="60"
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      class="adjustments-params__left-block"
                      :top="$t('addressProgramGeoPointInfo')"
                      :bottom="$t('addressProgramGeoPointDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('area')"
                            :bottom="outletData.area"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('city')"
                            :bottom="outletData.city"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('geoCluster')"
                            :bottom="outletData.clusterValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('hsr')"
                            :bottom="outletData.hsrValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('tdm')"
                            :bottom="outletData.tdmValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('kadl')"
                            :bottom="outletData.kadlValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
<!--                Классификационные параметры -->
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  isHideRight
                  :gap="60"
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      class="adjustments-params__left-block"
                      :top="$t('addressProgramClassifiersParamsInfo')"
                      :bottom="$t('addressProgramClassifiersParamsDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('externalFormat')"
                            :bottom="outletData.externalFormatValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('outletType')"
                            :bottom="outletData.outletTypeValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('channel')"
                            :bottom="outletData.channelValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('narka')"
                            :bottom="outletData.narkaValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('sIPChannel')"
                            :bottom="outletData.sipChannelValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderTerritoryCluster')"
                            :bottom="outletData.territoryCluster"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                        style="margin-top: 10px"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('skyline')"
                            :bottom="outletData.skyline"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('externalCode')"
                            :bottom="outletData.externalCode"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('tariffZone')"
                            :bottom="outletData.tariffZone"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
<!--                Параметры агентства -->
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  isHideRight
                  :gap="60"
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      class="adjustments-params__left-block"
                      :top="$t('addressProgramAgencyParamsInfo')"
                      :bottom="$t('addressProgramAgencyParamsDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('agency')"
                            :bottom="outletData.agencyValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('kAS')"
                            :bottom="outletData.kasValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('merchandiser')"
                            :bottom="outletData.merchandiserValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('routeCode')"
                            :bottom="outletData.routeCodeValue"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
<!--                KPI по покрытию -->
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  isHideRight
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      class="adjustments-params__left-block"
                      :top="$t('openKpi')"
                      :bottom="$t('openKPI')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderRatePerHour')"
                            :bottom="formatDigit(outletData.ratePerHourPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('visitSum')"
                            :bottom="
                              formatDigit(outletData.frequencyPlan, null, 0)
                            "
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('timingPerVisit')"
                            :bottom="formatDigit(outletData.timingPerVisitPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('fteMerch')"
                            :bottom="formatDigit(outletData.fteMerchPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
<!--                Бизнесс KPI -->
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  isHideRight
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      class="adjustments-params__left-block"
                      :top="$t('businessKpi')"
                      :bottom="$t('addressProgramKPIDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('osa')}`"
                            :bottom="
                              getKpiValue(outletData.osaPlan * 100)
                            "
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('deltaISA-OSA')}`"
                            :bottom="formatDigit(outletData.deltaISAOSAPlan * 100)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderPogWetWHSPlan')}`"
                            :bottom="formatDigit(outletData.pogWetWHSPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderPogWetPFTPlan')}`"
                            :bottom="getKpiValue(outletData.pogWetPFTPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderPogWetSHBPlan')}`"
                            :bottom="getKpiValue(outletData.pogWetSHBPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderPogDryWHSPlan')}`"
                            :bottom="getKpiValue(outletData.pogDryWHSPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderPogDryPFTPlan')}`"
                            :bottom="getKpiValue(outletData.pogDryPFTPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderLSizePlan')}`"
                            :bottom="getKpiValue(outletData.lSizePlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderStripeCTDreamPlan')}`"
                            :bottom="getKpiValue(outletData.stripeCTDreamPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderStripePFTCatPlan')}`"
                            :bottom="
                              getKpiValue(outletData.stripePFTCatPlan)
                            "
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderStripePFDogPlan')}`"
                            :bottom="getKpiValue(outletData.stripePFDogPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderStripeSPPlan')}`"
                            :bottom="getKpiValue(outletData.stripeSPPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderCtShareDreamPlan')}`"
                            :bottom="getKpiValue(outletData.ctShareDreamPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderCtSharePFCatPlan')}`"
                            :bottom="getKpiValue(outletData.ctSharePFCatPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderCtSharePFDogPlan')}`"
                            :bottom="getKpiValue(outletData.ctSharePFDogPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderExSitePlan')}`"
                            :bottom="getKpiValue(outletData.exSitePlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="`${$t('addressProgramOrderHotZonePlan')}`"
                            :bottom="getKpiValue(outletData.hotZonePlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10" />
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
<!--                Бюджет -->
                <ProSpaceGeoLayout
                  class="geo-row"
                  style="padding-bottom: 0 !important;"
                  isHideBottom
                  isHideTop
                  isHideRight
                  :gap="60"
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      class="adjustments-params__left-block"
                      :top="$t('addressProgramBudgetInfo')"
                      :bottom="$t('addressProgramBudgetDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('visits')"
                            :bottom="
                              formatDigit(outletData.budgetVisitPlan)
                            "
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderOsa')"
                            :bottom="formatDigit(outletData.budgetOSAPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('deltaISA-OSA')"
                            :bottom="formatDigit(outletData.budgetDeltaISAOSAPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderPogWetWHSPlan')"
                            :bottom="formatDigit(outletData.budgetPOGWetWHSPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderPogWetPFTPlan')"
                            :bottom="formatDigit(outletData.budgetPOGWetPFTPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderPogWetSHBPlan')"
                            :bottom="formatDigit(outletData.budgetPOGWetSHBPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderPogDryWHSPlan')"
                            :bottom="formatDigit(outletData.budgetPOGDryWHSPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderPogDryPFTPlan')"
                            :bottom="formatDigit(outletData.budgetPOGDryPFTPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderLSizePlan')"
                            :bottom="formatDigit(outletData.budgetLSizePlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderStripeCTDreamPlan')"
                            :bottom="formatDigit(outletData.budgetStripeCTDreamPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderStripePFTCatPlan')"
                            :bottom="
                              formatDigit(outletData.budgetStripePFTCatPlan)
                            "
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderStripePFDogPlan')"
                            :bottom="formatDigit(outletData.budgetStripePFDogPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderStripeSPPlan')"
                            :bottom="formatDigit(outletData.budgetStripeSPPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderCtShareDreamPlan')"
                            :bottom="formatDigit(outletData.budgetCTShareDreamPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderCtSharePFCatPlan')"
                            :bottom="formatDigit(outletData.budgetCTSharePFCatPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderCtSharePFDogPlan')"
                            :bottom="formatDigit(outletData.budgetCTSharePFDogPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderExSitePlan')"
                            :bottom="formatDigit(outletData.budgetExSitePlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderHotZonePlan')"
                            :bottom="formatDigit(outletData.budgetHotZonePlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceVLayout :gap="10">
                          <ProSpaceTextBlock
                            :top="$t('addressProgramOrderTotalSum')"
                            :bottom="formatDigit(outletData.totalSumPlan)"
                            :gap="gapTextBlockCenter"
                            break-word
                            type="six"
                          />
                        </ProSpaceVLayout>
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
              </ProSpaceVLayout>
            </template>
          </ProSpaceTabs>
        </template>
        <template #right>
          <AdjustmentFormPanel
            v-if="modelTab.systemName === 'adjustmentRequest'"
            ref="formPanel"
            :adjustment="model"
            :isAdjustmentTab="true"
            :isProceeded="isProceeded"
            :periodId="currentPeriod.id"
          />
        </template>
      </ProSpaceGeoLayout>
    </template>
  </ProSpaceGeoLayout>
  <ProSpaceFlashMessage
    v-if="showConfirmModel"
    v-model="showConfirmModel"
    :type="confirmType"
    :icon="confirmIcon"
    :title="confirmTitle"
    :styleTitle="{ 'max-width': '65%' }"
    :applyTxt="confirmApplyTxt"
    :cancelTxt="confirmCancelTxt"
    @apply="applyConfirm"
    @cancel="cancelConfirm"
  >
    {{ confirmText }}
  </ProSpaceFlashMessage>
</template>
<script>
import {
  ProSpaceTopPanel,
  ProSpaceInputText,
  ProSpaceAction,
  ProSpaceRadioButtonSecondary,
  ProSpaceGeoLayout,
  ProSpacePanelBlock,
  ProSpacePanelBlockList,
  ProSpaceRightPanel,
  ProSpaceTabs,
  ProSpaceTextBlock,
  ProSpaceHLayout,
  ProSpaceVLayout,
  ProSpaceDropdown,
  ProSpaceFile,
  ProSpaceInputCalendar,
  ProSpaceInputNumber,
  ProSpaceSearchfield,
  ProSpaceMultiSelect,
  ProSpaceIcon,
  ProSpaceMainGrid,
  ProSpaceDots,
  ProSpaceTableHeaderCell,
  ProSpaceEmptyGrid,
  ProSpaceFlashMessage,
  ProSpaceModal,
  ProSpaceSelectButton,
  ProSpaceTag,
} from "@prospace/prospace-components-library";
import Column from "primevue/column";
import AdjustmentFormPanel from "./components/AdjustmentFormPanel";
import { AdjustmentService } from "../../services/AdjustmentService.js";
import { AdjustmentCommentService } from "../../services/AdjustmentCommentService";
import { mapGetters } from "vuex";
import accessMixin from "../../../../../Frontends/Common/utils/elementAccess/accessMixin";
import { AddressProgramOrderService } from "../../services/AddressProgramOrderService";
import formatDigit from "@prospace/prospace-components-library/src/lib-assets/mixins/digitFormatter";

export default {
  mixins: [accessMixin],
  components: {
    ProSpaceTopPanel,
    ProSpaceGeoLayout,
    ProSpaceInputText,
    ProSpaceAction,
    ProSpaceRadioButtonSecondary,
    ProSpacePanelBlock,
    ProSpacePanelBlockList,
    ProSpaceRightPanel,
    ProSpaceTabs,
    ProSpaceTextBlock,
    ProSpaceHLayout,
    ProSpaceVLayout,
    ProSpaceDropdown,
    ProSpaceFile,
    ProSpaceInputCalendar,
    ProSpaceInputNumber,
    ProSpaceSearchfield,
    ProSpaceMultiSelect,
    ProSpaceIcon,
    ProSpaceMainGrid,
    ProSpaceDots,
    ProSpaceTableHeaderCell,
    ProSpaceEmptyGrid,
    ProSpaceFlashMessage,
    ProSpaceModal,
    Column,
    ProSpaceSelectButton,
    AdjustmentFormPanel,
    ProSpaceTag,
  },
  data() {
    return {
      currentStatus: "approvalStatus",
      // Tabs
      modelTab: {
        name: this.$t("adjustmentRequest"),
        isActive: true,
        systemName: "adjustmentRequest",
      },
      modelTab1: {},

      // Common
      layoutName: "",
      model: null,
      editedFields: [],
      loading: false,
      skipCheckChanges: false,

      //Comments
      adjustmentCommentModalShow: false,
      adjustmentCommentModel: {
        comment: "",
        role: null,
        adjustmentId: null,
      },

      // Confirm Model
      showConfirmModel: false,
      confirmIcon: "",
      confirmType: "",
      confirmTitle: "",
      confirmText: "",
      confirmApplyTxt: "",
      confirmCancelTxt: "",
      applyConfirm: () => {},
      cancelConfirm: null,

      // Service
      adjustmentService: new AdjustmentService(),
      adjustmentCommentService: new AdjustmentCommentService(),
      addressProgramOrderService: new AddressProgramOrderService(),

      getMethod: "get",
      masterId: null,
      addressProgramId: null,
      outletData: {},
      percentageKeys: ["osaPlan", 'deltaISAOSAPlan'],
      formattingKeys: [
        "ratePerHourPlan",
        "ratePerHourFact",
        "timingPerVisitPlan",
        "timingPerVisitFact",
        "fteMerchPlan",
        "fteMerchFact",
        "osaPlan",
        "osaFact",
        "deltaISAOSAPlan",
        "deltaISAOSAFact",
        "visitSumPlan",
        "osaSumPlan",
        "totalSumPlan",
        "visitSumFact",
        "osaSumFact",
        "totalSumFact",
        "osaTargetPlanW",
        "osaTargetFactW",
        "osaReportPlanW",
        "osaReportFactW",
        "pogWetWHSPlan",
        "pogWetPFTPlan",
        "pogWetSHBPlan",
        "pogDryWHSPlan",
        "pogDryPFTPlan",
        "lSizePlan",
        "stripeCTDreamPlan",
        "stripePFTCatPlan",
        "stripePFDogPlan",
        "stripeSPPlan",
        "ctShareDreamPlan",
        "ctSharePFCatPlan",
        "ctSharePFDogPlan",
        "exSitePlan",
        "hotZonePlan"
      ],

      gapTextBlockCenter: 7,
    };
  },
  watch: {
    modelTab(val) {
      if (val && val.systemName === "adjustmentRequest") {
        setTimeout(() => {
          this.$refs.grid.loading = false;
        }, 500);
      }
    },
  },
  mounted() {
    this.loadRoleAccess();
    this.getData();
    window.addEventListener("beforeunload", this.beforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.beforeUnload);
  },
  beforeRouteLeave(to, from) {
    if (this.checkAdjustmentChanges()) {
      this.skipCheckChanges = true;
      this.showConfirmUnsavedChanges(
        () => {
          this.$router.push({ name: to.name });
        },
        () => {
          this.$parent.$parent.$parent.setActiveMenuitem(from);
          this.skipCheckChanges = false;
        }
      );
      return false;
    }

    return true;
  },
  methods: {
    formatDigit,
    getKpiValue(value) {
      if (!value) {
        return "";

      }
      if (!isNaN(value) && value.toString().includes(".")) {
        value = this.formatDigit(value, null, 2);
      }
      return value;
    },
    beforeUnload(event) {
      if (this.checkAdjustmentChanges()) {
        event.returnValue = "";
        event.preventDefault();
      }
    },
    gridLoaded() {},
    setTitle(title) {
      if (this.isGrouped) {
        title
          ? (this.layoutName += `${title} | ${this.$t(
              "adjustmentRequestGrouped"
            )}`)
          : this.$t("adjustmentRequestGrouped");
      } else {
        title
          ? (this.layoutName += `${title} | ${this.$t("adjustmentRequest")}`)
          : this.$t("adjustmentRequest");
      }
    },
    async getData() {
      this.masterId = this.$route.params.id;
      let title = this.$route.params.agencyValue;
      if (this.masterId === undefined) {
        // redirect to 404
        return;
      }
      this.setTitle(title);
      const result = await this.adjustmentService[this.getMethod](
        this.masterId,
        this.currentPeriod.id
      );
      this.editedFields = JSON.parse(result.data.editedFieldsConfig);
      this.adjustmentService.editedFields = this.editedFields;
      this.model = result.data;
      if (!title) {
        this.setTitle(this.model.agencyValue);
      }
      if (this.model.status === "onacceptance") {
        this.currentStatus = "acceptanceStatus";
      } else if (this.model.status === "onapproval") {
        this.currentStatus = "approvalStatus";
      }

      const request = await this.addressProgramOrderService[this.getMethod](
        result.data.addressProgramOrderId
      );
      this.outletData = request.data;
      this.$refs.grid.loading = false;
    },
    handleDots(action) {
      var option = this.dotsOptions.find((o) => o.name === action);
      option.method();
    },
    saveAndClose() {
      this.save(true);
    },
    save(closeAfter = false) {
      this.loading = true;
      this.model.editedFieldsConfig = JSON.stringify(
        this.editedFields.map((item) => {
          item[this.currentStatus] =
            item[this.currentStatus] === "true" ||
            item[this.currentStatus] === true;
          return item;
        })
      );
      this.adjustmentService[
        this.model.status === "onacceptance"
          ? "acceptAdjustment"
          : "approveAdjustment"
      ](this.model).then(() => {
        this.getData();
        this.loading = false;

        if (closeAfter) this.backHandler();
      });
    },
    accept(row) {
      row.status = "adjustmentFieldAccepted";
    },
    decline(row) {
      row.status = "adjustmentFieldDeclined";
    },
    cancel(row) {
      row.status = "adjustmentFieldRequested";
    },
    showConfirmUnsavedChanges(action, cancelAction = () => {}) {
      this.confirmType = "default";
      this.confirmIcon = "flash-default";
      this.confirmTitle = this.$t("exitFromPageConfirmTxt");
      this.confirmText =
        this.$t("unsavedChangesTxt") + "\n" + this.$t("exitFromPageTxt");
      this.confirmApplyTxt = this.$t("confirmTxt");
      this.confirmCancelTxt = this.$t("cancelTxt");
      this.applyConfirm = action;
      this.cancelConfirm = cancelAction;
      this.showConfirmModel = true;
    },
    showConfirmAcceptAll() {
      this.confirmType = "default";
      this.confirmIcon = "flash-success";
      this.confirmTitle = this.$t("adjustmentAcceptAll");
      this.confirmText = this.$t("adjustmentParametersAcceptAllQuestion");
      this.confirmApplyTxt = this.$t("confirmTxt");
      this.confirmCancelTxt = this.$t("cancelTxt");
      this.applyConfirm = () => {
        this.acceptAll();
      };
      this.showConfirmModel = true;
    },
    showConfirmDeclineAll() {
      this.confirmType = "default";
      this.confirmIcon = "circle-close";
      this.confirmTitle = this.$t("adjustmentDeclineAll");
      this.confirmText = this.$t("adjustmentParametersDeclineAllQuestion");
      this.confirmApplyTxt = this.$t("confirmTxt");
      this.confirmCancelTxt = this.$t("cancelTxt");
      this.applyConfirm = () => {
        this.declineAll();
      };
      this.showConfirmModel = true;
    },
    acceptAll() {
      this.loading = true;
      this.editedFields.forEach((f) => {
        f[this.currentStatus] = true;
      });
      this.save();
    },
    declineAll() {
      this.loading = true;
      this.editedFields.forEach((f) => {
        f[this.currentStatus] = false;
      });
      this.save();
    },
    resetAll() {
      this.editedFields.forEach((f) => (f[this.currentStatus] = null));
    },
    checkIsProceeded(status) {
      return status !== "adjustmentFieldRequested";
    },
    backHandler() {
      if (this.checkAdjustmentChanges()) {
        this.skipCheckChanges = true;
        this.showConfirmUnsavedChanges(
          () => {
            this.back();
          },
          () => {
            this.skipCheckChanges = false;
          }
        );
      } else {
        this.back();
      }
    },
    back() {
      this.$router.go(-1);
    },
    getStatus(status) {
      if (!status) return;

      let type = "default";
      switch (status) {
        case "adjustmentFieldRequested":
          type = "default";
          break;
        case "adjustmentFieldAccepted":
          type = "teal";
          break;
        case "adjustmentFieldDeclined":
          type = "danger";
          break;
      }

      return {
        type: type,
        text: this.$t(status),
      };
    },
    checkAdjustmentChanges() {
      if (this.skipCheckChanges) return false;

      let prevValues = JSON.parse(this.model.editedFieldsConfig);

      return this.editedFields.some(
        (x) => prevValues.find((f) => f.key === x.key).status !== x.status
      );
    },
    selectAddressProgram(addressProgram) {
      this.addressProgramId = addressProgram.id;
    },
    showAdjustmentCommentModal() {
      this.adjustmentCommentModel.comment = "";
      this.adjustmentCommentModel.role = JSON.parse(
        localStorage.getItem("userRole")
      ).displayName;
      this.adjustmentCommentModalShow = true;
    },
    addComment() {
      this.loading = true;
      let commentsToAdd = [
        Object.assign(_.cloneDeep(this.adjustmentCommentModel), {
          adjustmentId: this.model.id,
        }),
      ];

      this.adjustmentCommentService
        .createMany(commentsToAdd, this.currentPeriod.id)
        .then(() => {
          this.$refs.formPanel.getComments();
          this.declineAll();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getFieldOldValue(data) {
      if (!data) return null;
      let value = data.oldDisplayValue || data.oldValue;
      if (data.key == 'coverage') {
        value = this.$t(`coverage_${data.oldValue.toString().toLowerCase()}`);
      }
      if (!isNaN(value) && String(value).includes('.')) {
        value = parseFloat(value).toFixed(2);
      } else if (!isNaN(value.replace(',', '.')) && String(value.replace(',', '.')).includes('.')) {
        value = parseFloat(value.replace(',', '.')).toFixed(2);
      }
      if (this.formattingKeys.includes(data.key)) {
        value = formatDigit(value);
      }
      if (!isNaN(value) && this.percentageKeys.includes(data.key)) {
        value += '%';
      }
      return value;
    },
    getFieldNewValue(data) {
      if (!data) return null;
      let value = data.displayValue || data.newValue;
      if (data.key == 'coverage') {
        value = this.$t(`coverage_${data.newValue}`);
      }
      if (!isNaN(value) && String(value).includes('.')) {
        value = parseFloat(value).toFixed(2);
      }
      if (this.formattingKeys.includes(data.key)) {
        value = formatDigit(value);
      }
      if (!isNaN(value) && this.percentageKeys.includes(data.key)) {
        value += '%';
      }
      return value;
    },
  },
  computed: {
    ...mapGetters({
      access: "securityRoleAccess/access",
      currentPeriod: "marsPeriodStore/getMarsPeriod",
    }),
    checkAccessApproval() {
      return (
        this.checkAccess("adjustment-approval") &&
        this.model.status === "onapproval"
      );
    },
    checkAccessAcceptance() {
      return (
        this.checkAccess("adjustment-acceptance") &&
        this.model.status === "onacceptance"
      );
    },
    showDotsInPanel() {
      if (this.model?.status === "completed") return false;
      if (
        this.model?.status === "onacceptance" &&
        this.checkAccess("adjustment-acceptance")
      )
        return true;
      else if (
        this.model?.status === "onapproval" &&
        this.checkAccess("adjustment-approval")
      )
        return true;
    },
    isSaveDisabled() {
      return (
        this.model.status === "completed" ||
        this.editedFields.some((obj) => obj[this.currentStatus] === null)
      );
    },
    tabs() {
      return [
        {
          name: this.$t("adjustmentRequest"),
          systemName: "adjustmentRequest",
          isActive: this.modelTab.systemName === "adjustmentRequest",
        },
        {
          name: this.$t("addressProgramOutletInfo"),
          systemName: "addressProgramOutletInfo",
          isActive: this.modelTab.systemName === "addressProgramOutletInfo",
        },
      ];
    },
    tabs1() {
      return [
        {
          name: this.$t("generalInformationAboutTheOutlet"),
          systemName: "adjustmentReqgeneralInformationAboutTheOutletuest",
          isActive: this.modelTab.systemName === "adjustmentRequest",
        },
        {
          name: "",
          systemName: "adjustmentReqgeneralInformationAboutTheOutletuest",
          isHidden: true,
        },
      ];
    },
    dotsOptions() {
      let res = [
        {
          name: "acceptAll",
          text: this.$t("adjustmentAcceptAll"),
          icon: "circle-check",
          method: this.showConfirmAcceptAll,
          iconSize: "15px",
          iconGap: 5,
          itemClassName:
            "prospace-dots-item--h34 adjustment-item__dots_itemMenu",
        },
        {
          name: "declineAll",
          text: this.$t("adjustmentDeclineAll"),
          icon: "ban",
          method: this.showConfirmDeclineAll,
          iconSize: "20px",
          iconGap: 5,
          itemClassName: "prospace-dots-item--h34",
        },
        {
          name: "resetAll",
          text: this.$t("adjustmentResetAll"),
          icon: "rotate-left-file",
          method: this.resetAll,
          iconSize: "15px",
          last: true,
          iconGap: 5,
          disabled: this.isDisabledReset,
          itemClassName:
            "prospace-dots-item--h34 adjustment-item__dots_itemMenu",
        },
      ];
      return res;
    },
    isProceeded() {
      return this.model && this.model.status === "adjustmentComplete";
    },
    isGrouped() {
      return this.model && this.model.type === "adjustmentGroupTT";
    },
    isAdjustmentTab() {
      return this.modelTab.systemName === "adjustmentRequest";
    },
    getAdjustmentEditedFields() {
      return this.adjustmentService.editedFields;
    },
    isDisabledReset() {
      return (
        this.currentStatus === "completed" ||
        this.editedFields.every((obj) => obj[this.currentStatus] === null)
      );
    },
    isCancelDisabled() {
      return !this.checkAccessApproval && !this.checkAccessAcceptance;
    },
  },
};
</script>
<style lang="scss">
.adjustment-item {
  &__inner-tabs.prospace-tabs {
    .prospace-tabs__header.prospace-h-layout {
      margin: 0 5px;
    }
  }
  .prospace-tabs__content {
    height: auto;
    .p-datatable {
      .p-datatable-tbody {
        min-width: 880px;
        > tr > td:first-child {
          padding-left: 10px;
        }
      }
      .p-datatable-thead {
        min-width: 880px;
        border-left: var(--prospace-ui-border);
        border-top-right-radius: 5px;
        border-top-left-radius: 5px;
      }
    }
    .p-selectable-row,
    .p-datatable .p-datatable-tbody > tr:not(.p-datatable-emptymessage) {
      width: calc(100% - 5px) !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    .main-grid {
      height: 260px !important;
      overflow: hidden;
      border-top-right-radius: 5px;
      border-top-left-radius: 5px;
      .p-datatable .p-datatable-thead .p-column-resizer {
        transform: scale(1, 1);
      }
    }
  }
  .p-datatable-wrapper,
  &__view-adjustment .p-datatable .p-datatable-tbody {
    background-color: unset;
  }
  &__values {
    align-items: center !important;
  }
  &__action {
    width: auto !important;
    .prospace-h-layout {
      justify-content: flex-end !important;
    }
    .prospace-action-text {
      font-size: 12px;
      flex: 0 0 auto;
    }
    .prospace-icon + .prospace-action-text {
      flex: 0 0 calc(100% - 20px);
    }
    .prospace-icon {
      flex: 0 0 20px;
    }
  }
  &__status-cell {
    min-width: 384px;
  }
  &__acceptance-cell {
    padding-top: 0;
  }
  .p-datatable-tbody {
    .adjustment-item__acceptance-cell {
      padding-top: 0 !important;
    }
    & > tr:not(.p-datatable-emptymessage) > td:last-child {
      padding-left: 10px;
    }
  }
  .adjustment-item__dots_itemMenu .prospace-icon {
    padding: 0 10px;
  }
  .adjustments-grid {
    &__select-btn {
      .prospace-select-button {
        .p-button {
          height: 26px !important;
          padding: 5px 7px;
        }
      }
      .prospace-select-button__icon-container {
        height: 16px !important;
        width: 16px !important;

        .prospace-icon {
          height: 20px !important;
          width: 20px !important;
          flex: 0 0 20px;
        }
      }
      .prospace-label {
        font-size: 11px !important;
      }
      .p-button.p-highlight:first-child {
        background: #fbe9e7 !important;
        border-color: #ee6d62 !important;
        .prospace-label {
          color: #ee6d62 !important;
        }
        .prospace-icon {
          background-color: #ee6d62 !important;
        }
      }
      .p-button.p-highlight:last-child {
        background: #e8f5e9 !important;
        border-color: #27ae60 !important;
        .prospace-label {
          color: #27ae60 !important;
        }
        .prospace-icon {
          background-color: #27ae60 !important;
        }
      }
    }
  }
  .adjustments-params {
    &__rows {
      & > .geo-row {
        grid-template-columns: 220px minmax(306px, 958px) !important;
        grid-template-areas: "left center";
        width: auto;
        .prospace-text-block {
          min-height: 38px;
        }
      }
      &--details {
        & > .geo-row {
          padding-top: 0;
        }
      }
    }
    &__left-block .prospace-text-block__bottom {
      color: var(--prospace-ui-icon-gray);
    }
  }
}
</style>
